<template>
  <nav-menu ref="menu" :name="name" :icon="icon" :value.sync="value" v-bind="$attrs" v-on="$listeners">
    <div v-if="$refs.menu?.isLoaded && value" style="width: 360px; min-width: min(100%, 360px); max-width: 100%;">

      <!-- A single menu that re-anchors to the hovered row, instead of one v-menu per subcategory -->
      <v-menu
        offset-x right max-width="320px"
        :value="!!submenu.child"
        :activator="submenu.activator"
        @input="v => v || closeSubmenu()"
      >
        <v-list
          v-if="submenu.child"
          class="nav-menu-content pb-4"
          @mouseenter.native="cancelCloseSubmenu"
          @mouseleave.native="scheduleCloseSubmenu"
        >
          <v-list-item
            class="li-pop ec-dark-blue--text mb-4"
            exact
            :to="{ name: 'catalog', params: { path: submenu.child.getPath([ fragment ]) } }"
          >
            <div />
            <div class="ml-n6 design-stack-title font-weight-bold">Search all {{submenu.child.title}}</div>
            <v-icon small v-html="mdiChevronRight" />
          </v-list-item>
          <v-list-item
            v-for="child in submenu.child.featuredStatic" :key="'nested-' + child.fragment"
            class="li-pop design-stack ec-dark-blue--text pl-4"
            exact
            :to="{ name: 'catalog', params: { path: child.getPath([ fragment ]) } }"
          >
            <div class="design-stack-preview">
              <catalog-preview-eager v-for="i in [ 2, 1, 0 ]" :key="i" class="white" :design="child.preview[i]" />
            </div>
            <div class="design-stack-title">
              <!-- Safari ignores '::first-letter' on the line-clamped ('-webkit-box') child, so the class goes on a block inside it -->
              <div>
                <div class="first-letter-uppercase">{{stripSuffix(child.title)}}</div>
              </div>
              <div class="text--label-active">{{child.seasonal | dateRange}}</div>
            </div>
            <v-icon small v-html="mdiChevronRight" />
          </v-list-item>
        </v-list>
      </v-menu>

      <v-list class="transparent" dense>
        <v-list-item
          class="mb-6 li-pop ec-dark-blue--text font-weight-bold"
          exact
          :to="{ name: 'catalog', params: { path: [ fragment ] }, query: { autofocus: 1 } }"
        >
          <v-icon small v-html="mdiMagnify" />
          <div>Search all {{name.split('/')[0].trim().toLowerCase()}}</div>
          <v-icon small v-html="mdiChevronRight" />
        </v-list-item>

        <v-list-item
          v-for="child in featured" :key="child.fragment"
          class="li-pop design-stack ec-dark-blue--text"
          v-bind:class="{ seasonal: child.seasonalScore > 0 }"
          exact
          :to="{ name: 'catalog', params: { path: child.getPath([ fragment ]) } }"
          @mouseenter.native="!isRight && openSubmenu(child, $event.currentTarget)"
          @mouseleave.native="!isRight && scheduleCloseSubmenu()"
        >
          <div class="design-stack-preview">
            <catalog-preview-eager
              v-for="i in [ 2, 1, 0 ]" :key="'featured-' + i"
              class="white"
              :design="(canLoadHidden || !i) ? child.preview[i] : null"
              @loaded="nLoaded++"
            />
          </div>
          <div class="design-stack-title">
            <div>
              <div class="first-letter-uppercase">{{stripSuffix(child.title)}}</div>
            </div>
            <div class="text--label-active">{{child.seasonal | dateRange}}</div>
          </div>
          <v-icon class="d-none-no-hover" small v-html="(!isRight && child.children.length) ? mdiMenuRight : mdiChevronRight" />
          <v-icon class="d-none-hover" small v-html="mdiChevronRight" />
        </v-list-item>

        <v-list-item
          class="li-pop ec-dark-blue--text"
          exact
          :to="{ name: 'catalog-categories', params: { type: fragment } }"
        >
          <v-icon small v-html="mdiDotsHorizontal" />
          <div class="design-stack-title">More categories</div>
          <v-icon small v-html="mdiChevronRight" />
        </v-list-item>

        <v-list-item
          class="mt-6 mb-4 li-pop design-stack ec-dark-blue--text"
          exact
          :to="{ name: 'catalog', params: { path: [ fragment, 'design-your-own' ] } }"
        >
          <div class="design-stack-preview">
            <catalog-preview-eager
              v-for="i in [ 2, 1, 0 ]" :key="'custom-' + i"
              class="white"
              :design="(canLoadHidden || !i) ? DESIGN_YOUR_OWN_PREVIEW[i] : null"
              @loaded="nLoaded++"
            />
          </div>
          <div class="design-stack-title">Design your own</div>
          <v-icon small v-html="mdiChevronRight" />
        </v-list-item>
      </v-list>

    </div>
  </nav-menu>
</template>


<style scoped>
.li-pop.design-stack {
  &.seasonal > .design-stack-title > div:first-child {
    font-weight: 700;
  }

  &:nth-last-child(1 of .seasonal) {
    margin-bottom: 24px;
  }
}
</style>


<script>
import CatalogPreviewEager from '@/components/CatalogPreviewEager.vue';
import NavMenu from '@/components/NavMenu.vue';
import dateRange from '@/filters/date-range.js';
import categoryTree from '@/mixins/category-tree.js';
import { mdiMagnify, mdiChevronRight, mdiDotsHorizontal, mdiMenuRight } from '@mdi/js';


const DESIGN_YOUR_OWN_PREVIEW = [
  { key: 'r/41e06ef8-02e2-4c43-a3d1-ac2b76062a4d', type: 'image/jpeg' },
  { key: 'r/d44077fb-0ec9-40f0-8d2a-1e439b3468f1', type: 'image/jpeg' },
  { key: 'r/05d84aad-8a38-4d14-8b67-c9edfd961016', type: 'image/jpeg' }
];

const HAS_HOVER = matchMedia('(hover: hover)').matches;

export default {
  name: 'nav-menu-category',

  components: {
    CatalogPreviewEager,
    NavMenu
  },

  mixins: [ categoryTree ],

  filters: { dateRange },

  props: [ 'name', 'fragment', 'icon', 'isRight' ],

  data() {
    return {
      value: false,
      nLoaded: 0,
      submenu: { child: null, activator: null }
    };
  },

  created() {
    Object.assign(this, { DESIGN_YOUR_OWN_PREVIEW, mdiMagnify, mdiChevronRight, mdiDotsHorizontal, mdiMenuRight });
  },

  computed: {
    featured() {
      return this.categoryTree?.children.find(x => x.fragment == this.fragment)?.featured || [];
    },

    canLoadHidden() {
      return HAS_HOVER && (this.nLoaded >= (this.featured.length + 1));
    }
  },

  methods: {
    stripSuffix(str) {
      return str.replace(/ (cards|invitations)$/, '');
    },

    async openSubmenu(child, activator) {
      clearTimeout(this.submenuTimer);
      if (this.submenu.child == child)
        return;
      if (!child.children?.length)
        // No grandchildren to show - just make sure any other row's submenu closes
        return this.closeSubmenu();
      if (this.submenu.child) {
        // Close first so the menu re-measures its position against the new activator
        this.submenu = { child: null, activator: null };
        await this.$nextTick();
      }
      this.submenu = { child, activator };
    },

    closeSubmenu() {
      clearTimeout(this.submenuTimer);
      this.submenu = { child: null, activator: null };
    },

    scheduleCloseSubmenu() {
      // Grace period so the cursor can cross the gap between the row and the flyout
      clearTimeout(this.submenuTimer);
      this.submenuTimer = setTimeout(this.closeSubmenu, 150);
    },

    cancelCloseSubmenu() {
      clearTimeout(this.submenuTimer);
    }
  },

  watch: {
    'loadCategoryTreeStatus.pending': {
      immediate: true,
      async handler(v) {
        await this.$nextTick();
        this.$refs.menu.isLoaded = !v;
      }
    },

    value(v) {
      if (!v)
        this.closeSubmenu();
    }
  }
}
</script>
